import type { GetStaticPaths, NextPage } from 'next'

import { supportedLanguages } from '~/data/lang/settings'
import { artistNoArtworkLimiter } from '~/schemas/common'
import { sanityClientCdn } from '~/services/sanity'
import type { PageType } from '~/utils/urlResolver'
import { urlQuery } from '~/utils/urlResolver'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

import type { ArtistPageData } from './page/[page]'
import ArtistIndexPage, { getArtistPageProps } from './page/[page]'

const Artist: NextPage<ArtistPageData> = (props: ArtistPageData) => {
  return <ArtistIndexPage {...props} />
}

const getStaticPropsOriginal = async function getStaticProps(
  context: any,
  localizedData: any
) {
  try {
    return (await getArtistPageProps(context, localizedData)) as any
  } catch {
    return {
      notFound: true,
    } as any
  }
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const result = await getStaticPropsOriginal(context, localizedData)

    if ('props' in result) {
      return {
        props: result.props,
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () => {
  const limiter =
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'main' ? '' : '[0..10]'

  try {
    const artistPages = await sanityClientCdn.fetch<Array<{ url: PageType }>>(
      `*[(_type == 'artist' ${artistNoArtworkLimiter} && defined(slug.current) && (!defined(isVisible) || isVisible == true) && !(_id in path('drafts.**')))]
        ${limiter} {
        ${urlQuery}
      }`
    )

    // Add validation and cleaning of slugs
    const paths =
      artistPages
        ?.filter((p): p is { url: Required<PageType> } => {
          const slug = p?.url?.slug?.trim()
          return Boolean(slug) && !slug.includes('  ') && !slug.startsWith('/')
        })
        .flatMap(p => {
          const cleanSlug = p.url.slug
            .trim()
            .replace(/\s+/g, '-')
            .replace(/\/+/g, '/')
            .replace(/^\/+|\/+$/g, '')
            .toLowerCase()

          const languages = ['en', ...supportedLanguages]

          // Create two sets of paths: one with page param and one without
          return [
            // Regular artist pages without pagination
            ...languages.map(lang => ({
              params: {
                lang,
                slug: cleanSlug,
              },
            })),

            // Artist pages with pagination (starting with page 1)
            ...languages.map(lang => ({
              params: {
                lang,
                slug: cleanSlug,
                page: '1',
              },
            })),
          ]
        }) || []

    return {
      paths: paths,
      fallback: 'blocking',
    }
  } catch (error) {
    console.error('Error in getStaticPaths:', error)

    return {
      paths: [],
      fallback: 'blocking',
    }
  }
}

export default Artist
